import { create } from "zustand";
import { ExtendedProperty, INITIAL_PROPERTIES } from "../data/mockData";
import { localStorageUtils } from "../utils/localStorage";

interface Notification {
  id: string;
  type: "success" | "error" | "info";
  message: string;
}

interface UserState {
  name: string;
  email?: string;
  phoneNumber?: string;
  uid: string;
}

interface AppState {
  user: UserState | null;
  isAuthenticated: boolean;
  activeTab: "dashboard" | "compare" | "upload" | "chat";
  properties: ExtendedProperty[];
  comparisonList: string[];

  // Chat State
  isChatOpen: boolean;
  chatHistory: {
    role: "user" | "assistant";
    content: string;
    isAction?: boolean;
  }[];
  aiHint: string | null;

  // UI State
  isCmdPaletteOpen: boolean;
  notifications: Notification[];

  // Actions
  login: (userData: UserState) => void;
  logout: () => void;
  setTab: (tab: "dashboard" | "compare" | "upload" | "chat") => void;
  toggleCompare: (id: string) => void;
  toggleChat: (forceState?: boolean) => void;
  addChatMessage: (msg: {
    role: "user" | "assistant";
    content: string;
    isAction?: boolean;
  }) => void;
  uploadData: (data: ExtendedProperty[]) => void;
  toggleCmdPalette: (isOpen?: boolean) => void;
  addNotification: (
    type: "success" | "error" | "info",
    message: string,
  ) => void;
  removeNotification: (id: string) => void;
}

// Helper to load initial state
const loadAuthFromStorage = () => {
  const savedUser = localStorageUtils.get<UserState | null>(
    "ddre_user_session",
    null,
  );
  return {
    user: savedUser,
    isAuthenticated: !!savedUser,
  };
};

export const useStore = create<AppState>((set, get) => ({
  ...loadAuthFromStorage(), // Initialize from Storage
  // Persist active tab so refresh keeps the same page
  activeTab: localStorageUtils.get<string>("ddre_active_tab", "dashboard") as
    | "dashboard"
    | "compare"
    | "upload"
    | "chat",
  properties: INITIAL_PROPERTIES,
  comparisonList: [],
  isChatOpen: false,
  chatHistory: [
    {
      role: "assistant",
      content: "Hey, let's get started.",
    },
  ],
  notifications: [],
  aiHint: null,
  isCmdPaletteOpen: false,

  login: (userData) => {
    localStorageUtils.set("ddre_user_session", userData); // Save Session
    set({ isAuthenticated: true, user: userData });
  },

  logout: () => {
    localStorageUtils.clear();
    set({ isAuthenticated: false, user: null, activeTab: "dashboard" });
  },

  setTab: (tab) => {
    let hint = "";
    if (tab === "dashboard")
      hint = "I've aggregated 40 new data points. Want a summary?";
    if (tab === "compare")
      hint = "Select up to 3 properties to see the valuation matrix.";
    if (tab === "upload")
      hint = "I can parse CSV, JSON, or Excel files automatically.";
    if (tab === "chat")
      hint = "Ask me anything about your data or market trends.";

    set({ activeTab: tab, aiHint: hint });
    // Persist current tab so refresh lands on same page
    localStorageUtils.set("ddre_active_tab", tab);
    setTimeout(() => set({ aiHint: null }), 5000);
  },

  toggleCompare: (id) =>
    set((state) => {
      const exists = state.comparisonList.includes(id);
      if (!exists && state.comparisonList.length < 3) {
        get().addNotification("success", "Added to Comparison Matrix");
        return { comparisonList: [...state.comparisonList, id] };
      }
      if (exists)
        return {
          comparisonList: state.comparisonList.filter((pid) => pid !== id),
        };
      get().addNotification("error", "Comparison limit reached (Max 3)");
      return state;
    }),

  toggleChat: (forceState) =>
    set((state) => ({ isChatOpen: forceState ?? !state.isChatOpen })),

  addChatMessage: (msg) =>
    set((state) => ({ chatHistory: [...state.chatHistory, msg] })),

  uploadData: (data) =>
    set((state) => ({
      properties: [...state.properties, ...data],
      notifications: [
        ...state.notifications,
        {
          id: Date.now().toString(),
          type: "success",
          message: `Successfully ingested ${data.length} records`,
        },
      ],
    })),

  toggleCmdPalette: (isOpen) =>
    set((state) => ({ isCmdPaletteOpen: isOpen ?? !state.isCmdPaletteOpen })),

  addNotification: (type, message) => {
    const id = Date.now().toString();
    set((state) => ({
      notifications: [...state.notifications, { id, type, message }],
    }));
    setTimeout(() => get().removeNotification(id), 4000);
  },

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
}));
